<template>
  <div class="recruitment-apply-page flex flex-col justify-between min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="flex justify-between items-center px-8 py-4 bg-white shadow-sm">
      <div class="flex items-center space-x-3">
        <span class="font-bold text-xl text-gray-800">校园招聘在线报名</span>
      </div>
      <el-button type="text" @click="$router.push('/recruitment/portal')">返回官网首页</el-button>
    </header>

    <!-- Form Panel -->
    <main class="flex-grow max-w-2xl w-full mx-auto px-4 py-12">
      <el-card class="apply-card shadow-lg p-6" :loading="loading">
        <div class="text-center mb-8">
          <h2 class="text-2xl font-bold text-gray-800">{{ event.title || '活动报名' }}</h2>
          <p class="text-gray-500 mt-2 text-sm">请认真填写以下报名资料，带 * 为必填项</p>
        </div>

        <el-form :model="formData" :rules="formRules" ref="applyForm" label-position="top">
          <!-- 1. 核心标准字段 (同步至 Candidate 表) -->
          <el-row :gutter="20">
            <a-col :span="12">
              <el-form-item label="真实姓名" prop="name">
                <el-input v-model="formData.name" placeholder="请输入您的姓名" prefix-icon="el-icon-user"></el-input>
              </el-form-item>
            </a-col>
            <a-col :span="12">
              <el-form-item label="电子邮箱" prop="email">
                <el-input v-model="formData.email" placeholder="请输入您的电子邮箱" prefix-icon="el-icon-message"></el-input>
              </el-form-item>
            </a-col>
          </el-row>

          <!-- 2. 动态扩展自定义字段 -->
          <div v-for="field in dynamicFields" :key="field.field" class="mt-2">
            <!-- 文本输入框 -->
            <el-form-item
              v-if="field.type === 'input'"
              :label="field.label"
              :prop="field.field"
              :rules="[{ required: field.required, message: `请输入${field.label}`, trigger: 'blur' }]"
            >
              <el-input v-model="formData[field.field]" :placeholder="`请输入${field.label}`"></el-input>
            </el-form-item>

            <!-- 下拉框 -->
            <el-form-item
              v-else-if="field.type === 'select'"
              :label="field.label"
              :prop="field.field"
              :rules="[{ required: field.required, message: `请选择${field.label}`, trigger: 'change' }]"
            >
              <el-select v-model="formData[field.field]" :placeholder="`请选择${field.label}`" class="w-full">
                <el-option
                  v-for="opt in parseOptions(field.options)"
                  :key="opt"
                  :label="opt"
                  :value="opt"
                ></el-option>
              </el-select>
            </el-form-item>
          </div>

          <!-- 提交按钮 -->
          <div class="mt-8">
            <el-button type="primary" class="w-full submit-btn" :loading="submitting" @click="submitForm">
              提交报名信息
            </el-button>
          </div>
        </el-form>
      </el-card>
    </main>
  </div>
</template>

<script>
  import { getRecruitmentEventDetailApi, postRecruitmentApplyApi } from '@/api';

  export default {
    name: 'RecruitmentApply',
    data() {
      return {
        eventId: this.$route.params.eventId,
        loading: false,
        submitting: false,
        event: {},
        dynamicFields: [],
        formData: {
          name: '',
          email: '',
        },
        formRules: {
          name: [
            { required: true, message: '姓名不能为空', trigger: 'blur' },
            { min: 2, max: 15, message: '长度在 2 到 15 个字符', trigger: 'blur' }
          ],
          email: [
            { required: true, message: '电子邮箱不能为空', trigger: 'blur' },
            { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
          ],
        },
      };
    },
    mounted() {
      this.loadEventDetails();
    },
    methods: {
      async loadEventDetails() {
        this.loading = true;
        try {
          const res = await getRecruitmentEventDetailApi(this.eventId);
          if (res && res.code === 0) {
            this.event = res.data || {};
            // 解析动态配置字段
            if (this.event.applyFields) {
              this.dynamicFields = JSON.parse(this.event.applyFields);
              
              // 动态在 formData 中初始化对应 Key，避免 Vue 2 响应式检测失效
              this.dynamicFields.forEach(field => {
                this.$set(this.formData, field.field, '');
              });
            }
          } else {
            this.$message.error('未找到对应活动配置，或活动未发布');
            this.$router.push('/recruitment/portal');
          }
        } catch (e) {
          this.$message.error('加载活动详情失败');
        } finally {
          this.loading = false;
        }
      },
      parseOptions(optionsStr) {
        if (!optionsStr) return [];
        // 支持中英文逗号分隔
        return optionsStr.split(/,|，/).map(item => item.trim()).filter(Boolean);
      },
      submitForm() {
        this.$refs.applyForm.validate(async (valid) => {
          if (!valid) return false;

          this.submitting = true;
          try {
            // 组装报名请求，包含 candidate 核心数据及 applyData 的 JSON 表达
            const applyPayload = {
              name: this.formData.name,
              email: this.formData.email,
              applyData: JSON.stringify(this.formData),
            };

            const res = await postRecruitmentApplyApi(this.eventId, applyPayload);
            if (res && res.code === 0) {
              this.$message.success('报名成功！即将为您跳转到个人测评中心');
              setTimeout(() => {
                this.$router.push('/recruitment/dashboard');
              }, 1500);
            } else {
              this.$message.error(res?.message || '报名失败，请稍后重试');
            }
          } catch (e) {
            this.$message.error('提交报名请求网络异常');
          } finally {
            this.submitting = false;
          }
        });
      },
    },
  };
</script>

<style scoped>
  /* 基础布局样式 */
  .recruitment-apply-page {
    background-color: #f8fafc;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }

  .apply-card {
    border-radius: 20px;
    border: none;
  }

  .submit-btn {
    background: linear-gradient(135deg, #1890ff 0%, #4facfe 100%);
    border: none;
    font-weight: 600;
    padding: 12px 20px;
    border-radius: 12px;
    transition: all 0.3s;
  }
  .submit-btn:hover {
    box-shadow: 0 4px 15px rgba(24, 144, 255, 0.3);
    opacity: 0.95;
  }

  /* Tailwind 兼容辅助类 */
  .flex { display: flex; }
  .flex-col { flex-direction: column; }
  .justify-between { justify-content: space-between; }
  .items-center { align-items: center; }
  .flex-grow { flex-grow: 1; }
  .px-8 { padding-left: 32px; padding-right: 32px; }
  .py-4 { padding-top: 16px; padding-bottom: 16px; }
  .px-4 { padding-left: 16px; padding-right: 16px; }
  .py-12 { padding-top: 48px; padding-bottom: 48px; }
  .bg-white { background-color: #ffffff; }
  .shadow-sm { box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03); }
  .shadow-lg { box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02); }
  .space-x-3 > * + * { margin-left: 12px; }
  .font-bold { font-weight: 700; }
  .text-xl { font-size: 20px; }
  .text-2xl { font-size: 24px; }
  .text-gray-800 { color: #1a202c; }
  .text-gray-500 { color: #718096; }
  .text-sm { font-size: 14px; }
  .max-w-2xl { max-width: 672px; }
  .mx-auto { margin-left: auto; margin-right: auto; }
  .p-6 { padding: 24px; }
  .text-center { text-align: center; }
  .mb-8 { margin-bottom: 32px; }
  .mt-2 { margin-top: 8px; }
  .mt-8 { margin-top: 32px; }
  .w-full { width: 100%; }
</style>
